<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';

const route = useRoute();
const router = useRouter();

useHead({
  title: 'Key Features of ZapScale: The Easiest Customer Success Software',
  meta: [
    {
      name: 'description',
      content:
        "Unlock the power of ZapScale's Customer Success software with our robust features. Drive customer success, streamline processes, and scale efficiently. Read more.",
    },
    {
      property: 'og:description',
      content:
        "Unlock the power of ZapScale's Customer Success software with our robust features. Drive customer success, streamline processes, and scale efficiently. Read more.",
    },
    {
      property: 'og:title',
      content: 'Key Features of ZapScale: The Easiest Customer Success Software',
    },
    {
      property: 'og:url',
      content: 'https://www.zapscale.com' + route.path,
    },
    {
      property: 'og:image',
      content: 'https://www.zapscale.com/images/logo.png',
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ZapScale',
  description:
    'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
  url: 'https://www.zapscale.com/',
  logo: 'https://www.zapscale.com/images/Header_logo.svg',
  sameAs: [
    'https://www.facebook.com/zapscale',
    'https://www.linkedin.com/company/zapscale/',
    'https://twitter.com/ZapScale',
  ],
}));

const cardsArr = [
  {
    title: 'Know your customer better',
    features: ['Customer 360 view', 'Health Analytics', 'Account Segmentation', 'Outcome Tracking'],
    image: 'features/feature-0',
    tabGroupId: 'tab-group-1',
  },
  {
    title: 'Run your CS ops better',
    features: ['Email Campaigns', 'Task management', 'Success Playbook', 'AI Assistant'],
    image: 'features/feature-1',
    tabGroupId: 'tab-group-2',
  },
  {
    title: 'Get better business insights ',
    features: ['Business Overview', 'Prediction Alerts', 'Trend Analytics'],
    image: 'features/feature-2',
    tabGroupId: 'tab-group-3',
  },
];

const feature1 = ref({
  'Customer 360': [
    {
      id: 1,
      title: 'An eagle eye in a world full of fragmented data',
      bullets: [
        'Get a unified view of all customer data with integrated data through various customer touch points from your product, CRM, tickets, feature requests, billing and invoicing, emails and customer comms.',
        'Pull in your customer data via native integrations, global API or upload.',
        'Create views by ARR, role, location or custom fields to uncover actionable customer & user insights with the most powerful analytics.',
      ],
      image: 'customer-360.webp',
      width: '480',
      height: '439',
    },
  ],
  'Health Analytics': [
    {
      id: 1,
      title: 'Get a crystal-clear picture of your customers’ health and avoid surprise churns',
      bullets: [
        'Get customer health data (ZapScore) with a pre-built health score framework & 40 KPIs powered with data analytics.',
        'Customize your KPIs based on customer health groups or add custom KPIs specific to your vertical (MarTech, Sales Tech, HRTech, etc.) ',
        'Spot Customer Risks & Opportunities with AI and take preemptive actions to increase retention and upsell.',
      ],
      image: 'health-analytics.webp',
      width: '480',
      height: '439',
    },
  ],
  'Account Segmentation': [
    {
      id: 1,
      title: 'Not all customers are the same. How can their CS strategy be?',
      bullets: [
        'Segment customers, your way. Group them based on lifecycle stage, ACV, location, date of start, account type (high-touch, low-touch) and much more.',
        'Make your Customer Engagements more precise, purposeful, and powerful.',
        'Create automated playbooks for monitoring & engagement for each segment.',
      ],
      image: 'account-segmentation.webp',
      width: '450',
      height: '266',
    },
  ],
  'Outcome Tracking': [
    {
      id: 1,
      title: 'Leave behind your gut feelings, catch the drift by comparing business outcome targets vs achieved',
      bullets: [
        'Focus on more than just features. Create custom product outcome journeys for customer segments with a simple DIY tool.',
        "Set up targets and compare with achieved to find and resolve downward trends in real time before it's too late.",
        'Track key actions & workflows in onboarding, adoption, retention, and expansion journeys.',
      ],
      image: 'outcome-tracking.webp',
      width: '480',
      height: '347',
    },
  ],
});

const feature2 = ref({
  'Email Campaigns': [
    {
      id: 1,
      title: 'Strengthen Customer Relationships Through Personalized Communication',
      bullets: [
        'Create hyper-personalized email campaigns with customer-specific data. Add NPS and CSAT surveys to capture user feedback.',
        'Segment customers by criteria like ACV, renewal date and real-time behaviour to send the right message at the right time. Use AI-assisted writing to craft compelling emails for each segment.',
        'Launch targeted campaigns from anywhere - Customer List, Playbooks or Success Plans, to ensure strategic engagement.',
      ],
      image: 'email-campaigns.webp',
      width: '480',
      height: '383',
    },
  ],
  'Task Management': [
    {
      id: 2,
      title: 'See who needs your attention first',
      bullets: [
        'Built mindfully for CSMs & Team leads for enhanced productivity. Bi-directional sync with CRM to avoid redundancy.',
        'Access a single, tailored, and prioritized view of all of your tasks and actions for your portfolio. Create workflows to automate tasks for renewals, support, QBRs and more.',
        'Monitor CSM performance across the board.',
      ],
      image: 'task-management.webp',
      width: '480',
      height: '307',
    },
  ],
  'Success Playbook': [
    {
      id: 3,
      title: 'Never miss any important customer update and set up resolution steps - all in just a few minutes!',
      bullets: [
        'Streamline your Ops with custom triggers. Auto-create & assign tasks, actions, messages, and emails.',
        'Automate customer engagements & internal processes for improved consistency, efficiency, and scalability.',
        'Create success plays based on health, lifecycle events like QBRs, segments or risks, and opportunities.',
      ],
      image: 'success-playbook.webp',
      width: '480',
      height: '426',
    },
  ],
  'AI Assistant': [
    {
      id: 4,
      title: 'Embrace the AI assistant for heavy-lifting of your workload',
      bullets: [
        'Use ZapGPT to summarise account history, explore trends, write emails, analyse communication sentiment and more. ',
        'Increase productivity and drive customer engagement while reducing manual effort, allowing you to focus on higher-value tasks that accelerate growth and impact.',
      ],
      image: 'ai-assistant-logo.webp',
      width: '520',
      height: '311',
    },
  ],
});

const feature3 = ref({
  'Business Overview': [
    {
      id: 1,
      title: 'Create a customer command center',
      bullets: [
        'Pivot to a customer-focused model by aligning around a centralized command center dashboard.',
        'Track business health, NRR, churn, product usage, and feature usage to spot business gaps.',
        'Unlock a new pipeline source and showcase the impact of CS on revenue growth with automatic Upsell and Renewal deal creation in the CRM. ',
      ],
      image: 'business-overview.webp',
      width: '480',
      height: '335',
    },
  ],
  'Prediction Alerts': [
    {
      id: 1,
      title: 'A crystal ball for CS teams',
      bullets: [
        "Never let the ball drop with ZapScale's AI-based churn prediction model.",
        'Make informed decisions fuelled by 40+ dimensions.',
        'Identify business expansion opportunities & optimize account planning with rich customer data analysis & robust workflow capabilities.',
      ],
      image: 'prediction-alerts.webp',
      width: '480',
      height: '333',
    },
  ],
  'Trend Analytics': [
    {
      id: 1,
      title: 'Find out how your business moves across days, months and weeks',
      bullets: [
        'Monitor all product, health, revenue, and business trends.',
        'Analyze customers/users journey over time.',
        'Identify product and engagement challenges & wins.',
      ],
      image: 'trend-analytics.webp',
      width: '480',
      height: '347',
    },
  ],
});

const allowScroll = ref(true);

const selectedIndex1 = ref<number>(0);
const selectedIndex2 = ref<number>(0);
const selectedIndex3 = ref<number>(0);

function changeTabGroup1(index: number) {
  allowScroll.value = false;
  selectedIndex1.value = index;

  switch (index) {
    case 0:
      router.push({ path: '/features', hash: '#customer-360' });
      break;
    case 1:
      router.push({ path: '/features', hash: '#health-analytics' });
      break;
    case 2:
      router.push({ path: '/features', hash: '#account-segmentation' });
      break;
    case 3:
      router.push({ path: '/features', hash: '#outcome-tracking' });
      break;
    default:
      break;
  }
}

function changeTabGroup2(index: number) {
  allowScroll.value = false;
  selectedIndex2.value = index;

  switch (index) {
    case 0:
      router.push({ path: '/features', hash: '#email-campaigns' });
      break;
    case 1:
      router.push({ path: '/features', hash: '#task-management' });
      break;
    case 2:
      router.push({ path: '/features', hash: '#success-playbook' });
      break;
    case 3:
      router.push({ path: '/features', hash: '#ai-assistant' });
      break;
    default:
      break;
  }
}

function changeTabGroup3(index: number) {
  allowScroll.value = false;
  selectedIndex3.value = index;

  switch (index) {
    case 0:
      router.push({ path: '/features', hash: '#business-overview' });
      break;
    case 1:
      router.push({ path: '/features', hash: '#prediction-alerts' });
      break;
    case 2:
      router.push({ path: '/features', hash: '#trend-analytics' });
      break;
    default:
      break;
  }
}

onMounted(() => {
  function scrollToTabGroup(tabGroupId: string, tabIndex: string) {
    let tabGroup = document.getElementById(tabGroupId) as HTMLElement;

    if (!tabGroup) {
      return;
    }

    tabGroup.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // const y = tabGroup.getBoundingClientRect().top;

    if (tabGroupId === 'tab-group-1') {
      selectedIndex1.value = Number(tabIndex);
    }

    if (tabGroupId === 'tab-group-2') {
      selectedIndex2.value = Number(tabIndex);
    }

    if (tabGroupId === 'tab-group-3') {
      selectedIndex3.value = Number(tabIndex);
    }
  }
  const listElements = document.getElementsByClassName('list-element');

  Array.from(listElements).forEach((x) => {
    x.addEventListener('click', function () {
      allowScroll.value = true;

      const listElementId = x.id;
      switch (listElementId) {
        case 'tab-group-1-0':
          router.push({ path: '/features', hash: '#customer-360' });
          break;
        case 'tab-group-1-1':
          router.push({ path: '/features', hash: '#health-analytics' });
          break;
        case 'tab-group-1-2':
          router.push({ path: '/features', hash: '#account-segmentation' });
          break;
        case 'tab-group-1-3':
          router.push({ path: '/features', hash: '#outcome-tracking' });
          break;
        case 'tab-group-2-0':
          router.push({ path: '/features', hash: '#email-campaigns' });
          break;
        case 'tab-group-2-1':
          router.push({ path: '/features', hash: '#task-management' });
          break;
        case 'tab-group-2-2':
          router.push({ path: '/features', hash: '#success-playbook' });
          break;
        case 'tab-group-2-3':
          router.push({ path: '/features', hash: '#ai-assistant' });
          break;
        case 'tab-group-3-0':
          router.push({ path: '/features', hash: '#business-overview' });
          break;
        case 'tab-group-3-1':
          router.push({ path: '/features', hash: '#prediction-alerts' });
          break;
        case 'tab-group-3-2':
          router.push({ path: '/features', hash: '#trend-analytics' });
          break;
      }
    });
  });

  watch(
    () => route.hash,
    () => {
      if (route.hash && allowScroll.value) {
        const hash = route.hash;
        switch (hash) {
          case '#customer-360':
            scrollToTabGroup('tab-group-1', '0');
            break;
          case '#health-analytics':
            scrollToTabGroup('tab-group-1', '1');
            break;
          case '#account-segmentation':
            scrollToTabGroup('tab-group-1', '2');
            break;
          case '#outcome-tracking':
            scrollToTabGroup('tab-group-1', '3');
            break;
          case '#email-campaigns':
            scrollToTabGroup('tab-group-2', '0');
            break;
          case '#task-management':
            scrollToTabGroup('tab-group-2', '1');
            break;
          case '#success-playbook':
            scrollToTabGroup('tab-group-2', '2');
            break;
          case '#ai-assistant':
            scrollToTabGroup('tab-group-2', '3');
            break;
          case '#business-overview':
            scrollToTabGroup('tab-group-3', '0');
            break;
          case '#prediction-alerts':
            scrollToTabGroup('tab-group-3', '1');
            break;
          case '#trend-analytics':
            scrollToTabGroup('tab-group-3', '2');
            break;
          default:
            break;
        }
      } else {
        return;
      }
    },
    { immediate: true, deep: true },
  );
});
</script>

<template>
  <div>
    <div class="bg-[#242424]">
      <div class="container mx-auto">
        <div class="px-5 pb-52 pt-6 text-center md:h-[566px] md:px-16 md:pb-60 md:pt-16 lg:h-[550px] lg:px-48 xl:px-52">
          <h1
            class="font-merriweather text-2xl font-extrabold text-white max-md:leading-7 md:text-3xl md:font-extrabold lg:text-5xl"
          >
            Make your <span class="text-[#F2B743]">Customer Success</span> Better
          </h1>
          <h2
            class="mx-auto mt-8 font-merriweather text-lg font-bold text-[#fff] max-md:leading-5 md:mt-6 md:w-[455px] md:text-xl md:font-normal"
          >
            Key features that make ZapScale the easiest customer success platform.
          </h2>
          <NuxtLink
            to="/demo/#book-a-demo"
            class="mx-auto mt-8 flex h-[41px] w-fit items-center rounded-full bg-[#FF395B] px-3 py-2 text-xs font-bold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B] md:px-6 md:text-base"
          >
            Book a Free Demo
          </NuxtLink>
        </div>
      </div>
    </div>
    <div
      class="-mb-[52rem] -mt-44 flex flex-col justify-center gap-x-10 gap-y-6 px-11 md:-mb-[16rem] md:flex-row lg:-mb-[14rem]"
    >
      <div
        v-for="(card, index) in cardsArr"
        :key="index"
        class="flex flex-col items-center justify-center gap-y-2 rounded-3xl bg-[#fff] px-5 py-6 md:gap-y-5 md:py-10"
      >
        <NuxtImg
          quality="30"
          preload
          :placeholder="[240, 130]"
          :src="`/images/${card.image}.webp`"
          class="h-[8.125rem]"
          :alt="card.title"
          width="240"
          height="130"
        />
        <h2 class="max-w-4/5 md:max-w-3/4 mx-auto text-center text-lg font-extrabold text-[#3a3a3c] max-md:leading-5">
          {{ card.title }}
        </h2>
        <ul class="space-y-1">
          <li
            v-for="(feature, index) in card.features"
            :key="index"
            :id="card.tabGroupId + '-' + index"
            class="list-element cursor-pointer text-center text-sm font-medium text-[#3a3a3c] underline"
          >
            {{ feature }}
          </li>
        </ul>
      </div>
    </div>
    <div class="h-[50rem] bg-[#F4FBFF] md:h-[14.563rem]"></div>
    <div class="bg-[#F4FBFF] lg:h-fit" id="tab-group-1">
      <div class="container mx-auto">
        <div class="px-5 pb-5 pt-20 md:px-10 md:py-12">
          <h2 id="know-your-customers" class="font-merriweather text-3xl font-bold md:text-4xl">
            <span class="text-[#0070C0]">Know</span> Your Customers
          </h2>
          <div class="mt-5 w-full md:mt-11">
            <TabGroup :selected-index="selectedIndex1" @change="changeTabGroup1">
              <TabList class="flex flex-row items-center justify-between border-b-[0.063rem]">
                <Tab v-for="category in Object.keys(feature1)" as="template" :key="category" v-slot="{ selected }">
                  <span
                    :class="[
                      'cursor-pointer text-center text-[13px] font-semibold leading-5 text-[#0070C0]  lg:text-xl',
                      'focus:outline-none',
                      selected ? 'border-b-[0.063rem] border-b-[#0070C0]' : 'text-[#98B5C5]',
                    ]"
                  >
                    {{ category }}
                  </span>
                </Tab>
              </TabList>

              <TabPanels class="">
                <TabPanel v-for="(contents, idx) in Object.values(feature1)" :key="idx" class="animate-appear pt-4">
                  <div class="flex flex-col justify-between gap-x-8 gap-y-6 md:flex-row">
                    <div class="md:w-1/2">
                      <div v-for="content in contents" :key="content.id" class="relative rounded-md md:p-3">
                        <h2 class="text-lg font-extrabold md:text-3xl xl:leading-9">
                          {{ content.title }}
                        </h2>

                        <ul
                          class="mt-4 list-disc space-y-3 pl-3 text-base font-medium leading-[21px] md:mt-6 lg:space-y-8"
                        >
                          <li v-for="(bullet, index) in content.bullets" :key="index">{{ bullet }}</li>
                        </ul>
                      </div>

                      <NuxtLink to="/demo/#book-a-demo">
                        <button
                          class="mt-4 rounded-full bg-[#0070C0] px-4 py-2 font-bold text-white md:mt-9 md:px-8 md:py-[0.938rem]"
                        >
                          Get Started
                        </button>
                      </NuxtLink>
                    </div>
                    <NuxtImg
                      :src="`/images/features/${contents[0].image}`"
                      :alt="contents[0].image"
                      :width="contents[0].width"
                      :height="contents[0].height"
                      class="md:h-1/2 md:w-[480px] md:pt-6"
                    />
                  </div>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-white lg:h-fit" id="tab-group-2">
      <div class="container mx-auto">
        <div class="px-5 py-5 md:px-10 md:py-12">
          <h2 id="run-your-cs-ops" class="font-merriweather text-3xl font-bold md:text-4xl">
            <span class="text-[#0070C0]">Automate</span> Your CS
          </h2>
          <div class="mt-5 w-full md:mt-11">
            <TabGroup :selected-index="selectedIndex2" @change="changeTabGroup2">
              <TabList class="flex w-full flex-row justify-between border-b-[0.063rem]">
                <Tab v-for="category in Object.keys(feature2)" as="template" :key="category" v-slot="{ selected }">
                  <span
                    :class="[
                      'cursor-pointer text-center text-[13px] font-semibold leading-5 text-[#0070C0] lg:text-xl',
                      'focus:outline-none',
                      selected ? 'border-b-[0.063rem] border-b-[#0070C0]' : 'text-[#98B5C5]',
                    ]"
                  >
                    {{ category }}
                  </span>
                </Tab>
              </TabList>

              <TabPanels class="">
                <TabPanel v-for="(contents, idx) in Object.values(feature2)" :key="idx" class="animate-appear pt-4">
                  <div class="flex flex-col justify-between gap-x-8 gap-y-6 md:flex-row-reverse">
                    <div class="md:w-1/2">
                      <div v-for="content in contents" :key="content.id" class="relative rounded-md md:p-3">
                        <h2 class="text-lg font-extrabold md:text-3xl xl:leading-9">
                          {{ content.title }}
                        </h2>

                        <ul
                          class="mt-4 list-disc space-y-3 pl-3 text-base font-medium leading-[21px] md:mt-6 lg:space-y-8"
                        >
                          <li v-for="(bullet, index) in content.bullets" :key="index">{{ bullet }}</li>
                        </ul>
                      </div>
                      <NuxtLink to="/demo/#book-a-demo">
                        <button
                          class="mt-4 rounded-full bg-[#0070C0] px-4 py-2 font-bold text-white md:mt-9 md:px-8 md:py-[0.938rem]"
                        >
                          Get Started
                        </button>
                      </NuxtLink>
                    </div>
                    <NuxtImg
                      :src="`/images/features/${contents[0].image}`"
                      :alt="contents[0].image"
                      :width="contents[0].width"
                      :height="contents[0].height"
                      class="md:w-[520px] md:pt-6"
                    />
                  </div>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-[#F4FBFF] lg:h-fit" id="tab-group-3">
      <div class="container mx-auto">
        <div class="px-5 py-5 md:px-10 md:py-12">
          <h2 id="get-better-business-insights" class="font-merriweather text-3xl font-bold md:text-4xl">
            Get Better<span class="text-[#0070C0]"> Business Insights</span>
          </h2>
          <div class="mt-5 w-full md:mt-11">
            <TabGroup :selected-index="selectedIndex3" @change="changeTabGroup3">
              <TabList class="flex flex-row justify-between border-b-[0.063rem] max-md:gap-x-2">
                <Tab v-for="category in Object.keys(feature3)" as="template" :key="category" v-slot="{ selected }">
                  <span
                    :class="[
                      'cursor-pointer text-center text-[13px] font-semibold leading-5 text-[#0070C0] lg:text-xl',
                      'focus:outline-none',
                      selected ? 'border-b-[0.063rem] border-b-[#0070C0]' : 'text-[#98B5C5]',
                    ]"
                  >
                    {{ category }}
                  </span>
                </Tab>
              </TabList>

              <TabPanels class="">
                <TabPanel v-for="(contents, idx) in Object.values(feature3)" :key="idx" class="animate-appear pt-4">
                  <div class="flex flex-col justify-between gap-x-8 gap-y-6 md:flex-row">
                    <div class="md:w-1/2">
                      <div v-for="content in contents" :key="content.id" class="relative rounded-md md:p-3">
                        <h2 class="text-lg font-extrabold md:text-3xl xl:leading-9">
                          {{ content.title }}
                        </h2>

                        <ul
                          class="mt-4 list-disc space-y-3 pl-3 text-base font-medium leading-[21px] md:mt-6 lg:space-y-8"
                        >
                          <li v-for="(bullet, index) in content.bullets" :key="index">{{ bullet }}</li>
                        </ul>
                      </div>
                      <NuxtLink to="/demo/#book-a-demo">
                        <button
                          class="mt-4 rounded-full bg-[#0070C0] px-4 py-2 font-bold text-white md:mt-9 md:px-8 md:py-[0.938rem]"
                        >
                          Get Started
                        </button>
                      </NuxtLink>
                    </div>
                    <NuxtImg
                      :src="`/images/features/${contents[0].image}`"
                      :alt="contents[0].image"
                      :width="contents[0].width"
                      :height="contents[0].height"
                      class="md:h-1/2 md:w-[480px] md:pt-6"
                    />
                  </div>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </div>
        </div>
      </div>
    </div>
    <div class="container mx-auto">
      <div class="px-5">
        <!-- <h2 class="text-center font-merriweather text-3xl font-bold text-[#3A3A3C] md:text-4xl">Trusted by many</h2> -->
        <NewTestimonials />
      </div>
    </div>
    <hr class="mx-auto h-[1px] w-3/5" />
    <div class="container mx-auto">
      <Compliance />
    </div>
    <div class="bg-[#f2bb2e]">
      <div class="container mx-auto">
        <div
          class="flex flex-col items-center justify-center gap-x-8 gap-y-2 px-5 py-10 text-center md:py-6 lg:flex-row"
        >
          <h2 class="font-merriweather text-3xl font-bold text-black md:text-4xl md:text-[35px]">
            Get started on your journey to make CS easy
          </h2>
          <NuxtLink
            to="/demo/#book-a-demo"
            class="w-fit rounded-full bg-[#3a3a3c] px-4 py-3 text-base font-semibold text-white md:font-bold"
          >
            Book a Demo
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
